import type { ReactNode } from "react";
import { GENRES, YEAR_RANGES, type YearRange } from "@/mocks/movies";

interface FilterPanelProps {
  activeGenre: string;
  activeYear: YearRange | null;
  sort: "default" | "updated";
  hasFilter: boolean;
  onGenreChange: (genre: string) => void;
  onYearChange: (range: YearRange | null) => void;
  onSortChange: (sort: "default" | "updated") => void;
  onClear: () => void;
}

function FilterGroup({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="border-t border-background-200 px-4 py-4 first:border-t-0 md:px-5">
      <h3 className="mb-3 text-sm font-bold text-foreground-900">{title}</h3>
      {children}
    </div>
  );
}

export default function FilterPanel({
  activeGenre,
  activeYear,
  sort,
  hasFilter,
  onGenreChange,
  onYearChange,
  onSortChange,
  onClear,
}: FilterPanelProps) {
  const chipBase =
    "rounded-md px-3 py-1.5 text-sm whitespace-nowrap transition-colors";

  return (
    <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
      <div className="flex items-center justify-between px-4 py-4 md:px-5">
        <h2 className="flex items-center gap-1.5 text-sm font-bold text-foreground-900">
          <i className="ri-filter-3-line text-primary-500"></i>
          筛选条件
        </h2>
        <button
          type="button"
          onClick={onClear}
          disabled={!hasFilter}
          className="text-xs text-foreground-400 transition-colors hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-50"
        >
          清空筛选
        </button>
      </div>

      <FilterGroup title="类型">
        <div className="flex flex-wrap gap-2">
          {["全部", ...GENRES].map((genre) => (
            <button
              key={genre}
              type="button"
              onClick={() => onGenreChange(genre)}
              className={`${chipBase} ${
                activeGenre === genre
                  ? "bg-primary-500 font-medium text-background-50"
                  : "bg-background-100 text-foreground-600 hover:bg-background-200"
              }`}
            >
              {genre}
            </button>
          ))}
        </div>
      </FilterGroup>

      <FilterGroup title="年份">
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => onYearChange(null)}
            className={`${chipBase} ${
              activeYear === null
                ? "bg-primary-500 font-medium text-background-50"
                : "bg-background-100 text-foreground-600 hover:bg-background-200"
            }`}
          >
            不限
          </button>
          {YEAR_RANGES.map((range) => (
            <button
              key={range.label}
              type="button"
              onClick={() => onYearChange(range)}
              className={`${chipBase} ${
                activeYear?.label === range.label
                  ? "bg-primary-500 font-medium text-background-50"
                  : "bg-background-100 text-foreground-600 hover:bg-background-200"
              }`}
            >
              {range.label}
            </button>
          ))}
        </div>
      </FilterGroup>

      <FilterGroup title="排序">
        <div className="flex flex-wrap gap-2">
          {[
            { value: "default" as const, label: "默认排序", icon: "ri-list-check" },
            { value: "updated" as const, label: "更新时间", icon: "ri-time-line" },
          ].map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => onSortChange(option.value)}
              className={`${chipBase} flex items-center gap-1.5 ${
                sort === option.value
                  ? "bg-accent-500 font-medium text-primary-950"
                  : "bg-background-100 text-foreground-600 hover:bg-background-200"
              }`}
            >
              <i className={option.icon}></i>
              {option.label}
            </button>
          ))}
        </div>
      </FilterGroup>
    </div>
  );
}